import { AxiosPromise, AxiosRequestConfig, AxiosResponse } from 'axios';
import { convertGetEVisitsEventsResponse } from 'pages/e-visits/e-visits/e-visits.util';
import { TBehavioralTimelineParams } from 'types/behavioral-data';
import {
  IEVisitEvents,
  IGetEventsParams,
  IOfficeHoursWeekRule,
  IOfficeHoursWeekRuleWithId,
  TEVisitOrder,
  TEVisitSlot,
} from 'types/e-visits';
import {
  TGetDrugsParams,
  TPostSendEmail,
  TPostSendNotification,
  TPostSendSms,
} from 'types/member-api';
import { TNotesState } from 'types/notes';
import { TUpdateTagData } from 'types/tags-page';
import { PatientListResponse } from '../../types/patients';
import BaseApi from './base/base-api';

class MemberApiV2 extends BaseApi {
  protected authApiURl: string;

  constructor(baseUrl: string) {
    super(baseUrl, {
      allowRefreshToken: true,
      includeAuthorization: true,
    });
    this.authApiURl = process.env.REACT_APP_AUTH_API_URL || '';
  }

  searchExpertise = (data: any) => this.axios.post(`/management/public/expertise/search`, data);

  getProviders = () => this.axios.get(`/management/public/user/providers`);

  createProvider = (values: Record<string, unknown>) => {
    const data = {
      name: values.name,
      address: values.streetNum,
      city: values.city,
      postCode: values.postal,
      country: 'SK',
      contactEmail: values.email,
      contactPhone: values.phone,
      iban: values.bankAccount,
      identificationNumber: values.regNum,
      taxIdentificationNumber: values.vatId,
      valueAddedTaxIdentificationNumber: 'nope',
    };

    return this.axios.post(`${this.baseURL}/management/public/provider/create`, data);
  };

  searchProviders = (data: Record<string, unknown>) =>
    this.axios.post(`${this.baseURL}/management/public/provider/search`, data);

  getProfile = () => this.axios.get(`${this.baseURL}/client/profile`);

  updateProfile = (data: any): Promise<AxiosPromise> =>
    this.axios.patch(`${this.baseURL}/client/profile`, data);

  requestToJoinProvider = (providerId: string) =>
    this.axios.post(`${this.baseURL}/management/public/provider/join/${providerId}`);

  responseToInvite = (providerId: string, state: 'approved' | 'rejected') =>
    this.axios.post(`${this.baseURL}/management/public/user/providers/${providerId}`, {
      state,
    });

  searchInstitutions = (providerId: string, data: any) =>
    this.axios.post(`${this.baseURL}/management/public/institution/search`, data, {
      headers: {
        'provider-id': providerId,
      },
    });

  responseClientPairing = (providerId: string, clientId: string, data: any) =>
    this.axios.post(`${this.baseURL}/management/private/client/pairing/${clientId}`, data, {
      headers: {
        'provider-id': providerId,
      },
    });

  getDoctorMessages = (providerId: string): Promise<AxiosPromise> =>
    this.axios.get(`${this.baseURL}/management/private/announcement?type=all`, {
      headers: {
        'provider-id': providerId,
      },
    });

  postSendEmail = (providerId: string, data: TPostSendEmail): Promise<AxiosPromise> =>
    this.axios.post(`${this.baseURL}/management/private/announcement?type=email`, data, {
      headers: {
        'provider-id': providerId,
      },
    });

  postSendNotification = (providerId: string, data: TPostSendNotification): Promise<AxiosPromise> =>
    this.axios.post(`${this.baseURL}/management/private/announcement?type=push`, data, {
      headers: {
        'provider-id': providerId,
      },
    });

  postSendSms = (providerId: string, data: TPostSendSms): Promise<AxiosPromise> =>
    this.axios.post(`${this.baseURL}/management/private/announcement?type=sms`, data, {
      headers: {
        'provider-id': providerId,
      },
    });

  // getStaff = (providerId: string, staffId: string) => {
  //   return this.axios.get(`${this.baseURL}/management/private/worker/${staffId}`, {
  //     headers: {
  //       'provider-id': providerId,
  //     },
  //   });
  // };
  //
  // approveStaff = () =>
  //   this.axios.post(`${this.baseURL}/management/private/worker/search`, data, {
  //     headers: {
  //       'provider-id': providerId,
  //     },
  //   });

  searchStaff = (providerId: string, data: any) =>
    this.axios.post(`${this.baseURL}/management/private/worker/search`, data, {
      headers: {
        'provider-id': providerId,
      },
    });

  changeStateStaff = (providerId: string, staffId: string) => {
    return this.axios.patch(
      `${this.baseURL}/management/private/worker/status/${staffId}`,
      {
        status: 'approved',
      },
      {
        headers: {
          'provider-id': providerId,
        },
      },
    );
  };

  changeRoleStaff = (providerId: string, staffId: string, role: string) => {
    return this.axios.patch(
      `${this.baseURL}/management/private/worker/role/${staffId}`,
      {
        role,
      },
      {
        headers: {
          'provider-id': providerId,
        },
      },
    );
  };

  getOfficeHours = (providerId: string, workspaceId: string, params?: string) => {
    return this.axios.get(
      `/management/private/calendar/workspace-hours/rules?workspaceId=${workspaceId}${
        params ?? ''
      }`,
      {
        headers: {
          'provider-id': providerId,
        },
      },
    );
  };

  /**
   * Get rules for office hours for given expertise and institution
   * @param workspaceId medical institution id
   * @returns list of rules
   */
  getOfficeHoursRules = async (providerId: string, workspaceId: string) =>
    this.axios.get(
      `/management/private/calendar/workspace-hours/rules?workspaceId=${workspaceId}`,
      {
        headers: {
          'provider-id': providerId,
        },
      },
    );

  /**
   * Create office hour rule
   * @param rule rule to create
   */
  createOfficeHoursRule = async (providerId: string, rule: IOfficeHoursWeekRule) =>
    this.axios.post(
      '/management/private/calendar/workspace-hours/rules',
      {
        ...rule,
      },
      {
        headers: {
          'provider-id': providerId,
        },
      },
    );

  /**
   * Update office hour rule
   * @param rule rule to update
   */
  updateOfficeHoursRule = async (providerId: string, rule: IOfficeHoursWeekRuleWithId) =>
    this.axios.put('/management/private/calendar/workspace-hours/rules', rule, {
      headers: {
        'provider-id': providerId,
      },
    });

  /**
   * Delete rule from now or in future
   * @param id id of rule
   * @param dateFrom Specify date to delete rule from
   * @returns promise
   */
  deleteOfficeHoursRule = async (providerId: string, id: string, dateFrom?: string) =>
    this.customV2('/management/private/calendar/workspace-hours/rules', providerId, {
      method: 'DELETE',
      params: { id, dateFrom },
    });

  getEVisitsEvents = async (
    providerId: string,
    params: IGetEventsParams,
  ): Promise<AxiosResponse<{ data: { dates: IEVisitEvents } }>> => {
    return this.customV2<{ events: (TEVisitSlot | TEVisitOrder)[] }>(
      '/management/private/calendar/events',
      providerId,
      {
        method: 'GET',
        params: {
          ...params,
          workspaceId: params.medicalInstitutionId,
        },
      },
    ).then(convertGetEVisitsEventsResponse);
  };

  /**
   * Function to leverage inner instance of axios to make call with interceptors based on configuration
   * @param path path to call
   * @param params axios request params
   * @returns axios promise
   */
  public customV2 = <T = any>(
    path: string,
    providerId: string,
    params?: Omit<AxiosRequestConfig, 'baseURL' | 'url'>,
  ): AxiosPromise<T> => {
    return this.axios({
      ...params,
      url: path,
      method: params?.method || 'GET',
      headers: {
        'provider-id': providerId,
      },
    });
  };

  getXmppCredentials = () => this.axios.get(`/client/profile/xmpp`);

  createOfficeHours = (providerId: string, data: any) => {
    return this.axios.post(`/management/private/calendar/workspace-hours/rules`, data, {
      headers: {
        'provider-id': providerId,
      },
    });
  };

  updateOfficeHours = (providerId: string, data: any) => {
    return this.axios.put(`/management/private/calendar/workspace-hours/rules`, data, {
      headers: {
        'provider-id': providerId,
      },
    });
  };

  getInsuranceProviders = (countryCode: string) =>
    this.axios.get(`${this.baseURL}/management/public/insurance/${countryCode}`);

  inviteStaff = (providerId: string, data: any) =>
    this.axios.post(`${this.baseURL}/management/private/worker/invite`, data, {
      headers: {
        'provider-id': providerId,
      },
    });

  getRoles = (providerId: string) =>
    this.axios.get('/management/private/provider/roles', {
      headers: {
        'provider-id': providerId,
      },
    });

  searchWorkspaces = (providerId: string, data: any) =>
    this.axios.post(`${this.baseURL}/management/private/workspace/search`, data, {
      headers: {
        'provider-id': providerId,
      },
    });

  getWorkspace = (providerId: string, workspaceId: string) => {
    return this.axios.get(`${this.baseURL}/management/private/workspace/${workspaceId}`, {
      headers: {
        'provider-id': providerId,
      },
    });
  };

  getWorkspaceTags = (providerId: string) =>
    this.axios.get(`${this.baseURL}/management/public/workspace/tags`);

  createWorkspace = (providerId: string, data: any) =>
    this.axios.post(`${this.baseURL}/management/private/workspace`, data, {
      headers: {
        'provider-id': providerId,
      },
    });

  updateWorkspace = (workspaceId: string, providerId: string, data: any) =>
    this.axios.put(`${this.baseURL}/management/private/workspace/${workspaceId}`, data, {
      headers: {
        'provider-id': providerId,
      },
    });

  searchPatients = (providerId: string, data: any) =>
    this.axios.post(`${this.baseURL}/management/private/client/search`, data, {
      headers: {
        'provider-id': providerId,
      },
    });

  getVitalDataById = (userId: string): Promise<AxiosPromise> =>
    this.axios.get(`${this.baseURL}/management/life-id/vital-data/summary`, {
      params: { userId },
    });

  getBehavioralDataById = (userId: string): Promise<AxiosPromise> =>
    this.axios.get(`${this.baseURL}/management/private/life-id/vital-data/summary`, {
      params: { userId },
    });

  getBehavioralTimelineDataById = (
    userId: string,
    type: string,
    params: TBehavioralTimelineParams,
  ): Promise<AxiosPromise> =>
    this.axios.get(`${this.baseURL}/management/private/life-id/behavioral-data/${type}/timeline`, {
      params: { ...params, userId },
    });

  getPatientList = (
    providerId: string,
    body: {
      query: string;
      pagination: {
        page?: number;
        sortBy?: 'createdAt' | 'name';
        direction?: 'ASC' | 'DESC';
      };
    },
  ): Promise<AxiosPromise<PatientListResponse>> => {
    return this.axios.post(`${this.baseURL}/management/private/client/search`, body, {
      headers: {
        'provider-id': providerId,
      },
    });
  };

  getGeneralInfoById = (providerId: string, id: string) => {
    return this.axios.get(`${this.baseURL}/management/private/life-id/general-info`, {
      headers: {
        'provider-id': providerId,
      },
      params: {
        userId: id,
      },
    });
  };

  getProfileById = (providerId: string, id: string) => {
    return this.axios.get(`${this.baseURL}/management/private/client/detail`, {
      headers: {
        'provider-id': providerId,
      },
      params: {
        clientId: id,
      },
    });
  };

  getDailyReport = (providerId: string, id: string, dateIso: string) => {
    return this.axios.get(`${this.baseURL}/management/private/life-id/vital-data/daily-report`, {
      headers: {
        'provider-id': providerId,
      },
      params: {
        userId: id,
        date: dateIso,
      },
    });
  };

  getVitalDataLastUpdateById = (providerId: string, id: string) => {
    return this.axios.get(`${this.baseURL}/management/private/life-id/last-update`, {
      headers: {
        'provider-id': providerId,
      },
      params: {
        userId: id,
      },
    });
  };

  searchUsers = (providerId: string, body: any): Promise<AxiosPromise> => {
    return this.axios.post(`${this.baseURL}/management/public/user/search`, body, {
      headers: {
        'provider-id': providerId,
      },
    });
  };

  getCovidScoreByDoctor = (providerId: string, id: string, pageSize = 1) => {
    return this.axios.post(
      `${this.baseURL}/management/private/life-id/vital-data/covid-score/search`,
      {
        id: null,
        source: null,
        from: null,
        to: null,
        page: 1,
        pageSize,
      },
      {
        headers: {
          'provider-id': providerId,
        },
        params: {
          userId: id,
        },
      },
    );
  };

  getQuestionnaires = (providerId: string, id: string) => {
    return this.axios.get(`${this.baseURL}/management/private/questionnaire/answer`, {
      headers: {
        'provider-id': providerId,
      },
      params: {
        userId: id,
      },
    });
  };

  getMedicalRecordsById = (providerId: string, id: string) => {
    return this.axios.get(`${this.baseURL}/management/private/medical-data/medical-record`, {
      headers: {
        'provider-id': providerId,
      },
      params: {
        clientId: id,
      },
    });
  };

  /**
   * Tags
   */
  // TODO: Should be reworked
  getTags = () =>
    this.axios.get(
      `${this.baseURL}/management/public/calendar/tags?page=1&pageSize=100&sortBy=name&direction=ASC&paginated=true`,
    );

  createTag = ({ data }: Partial<TUpdateTagData>) =>
    this.axios.post(`${this.baseURL}/management/public/calendar/tags`, data);

  updateTag = ({ id, data }: TUpdateTagData) =>
    this.axios.put(`${this.baseURL}/management/public/calendar/tags/${id}`, data);

  deleteTag = (id: string) =>
    this.axios.delete(`${this.baseURL}/management/public/calendar/tags/${id}`);
}

export const memberApiV2 = new MemberApiV2(process.env.REACT_APP_MEMBER_API_URL_V2 || '');
